import {
  AUDIT_ACTION_GROUP_KEYS,
  AUDIT_ACTOR_TYPES,
  type AuditActionGroup,
  type AuditActorType,
  type AuditLogItem,
  type ListAuditLogInput,
} from '@fos/shared/contracts/audit';
import { VIAS, type Via } from '@fos/shared/enums';
import { AUDIT_ACTOR_TYPE_LABELS, AUDIT_HIDDEN_REASON_LABELS } from '@fos/shared/labels';
import { toLocalDate } from '@fos/shared/time';

/**
 * Filtry dziennika w adresie strony (do udostępnienia i powrotu przyciskiem „Wstecz”):
 * `?kto=<userId>|ai|mcp|worker|system&rodzaj=<grupa>&akcja=<akcja>&obiekt=<typ>&klient=<id>&kanal=<via>&od=&do=`,
 * `wpis=<id>` otwiera szczegóły wpisu.
 */

export interface AuditFilterState {
  /** osoba (`user:<id>`) albo rodzaj aktora (`type:ai`) */
  who: string | null;
  group: AuditActionGroup | null;
  action: string | null;
  objectType: string | null;
  clientId: string | null;
  via: Via | null;
  from: string | null;
  to: string | null;
}

export const EMPTY_FILTERS: AuditFilterState = {
  who: null,
  group: null,
  action: null,
  objectType: null,
  clientId: null,
  via: null,
  from: null,
  to: null,
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

export function filtersFromParams(params: URLSearchParams): AuditFilterState {
  const kto = params.get('kto');
  const group = params.get('rodzaj');
  const via = params.get('kanal');
  const od = params.get('od');
  const doParam = params.get('do');
  const action = params.get('akcja');
  const objectType = params.get('obiekt');
  const clientId = params.get('klient');
  return {
    who: kto
      ? UUID.test(kto)
        ? `user:${kto}`
        : (AUDIT_ACTOR_TYPES as readonly string[]).includes(kto)
          ? `type:${kto}`
          : null
      : null,
    group: group && (AUDIT_ACTION_GROUP_KEYS as string[]).includes(group) ? (group as AuditActionGroup) : null,
    action: action && /^[a-z_][a-zA-Z0-9_.]*$/.test(action) ? action : null,
    objectType: objectType && /^[a-z_]+$/.test(objectType) ? objectType : null,
    clientId: clientId && UUID.test(clientId) ? clientId : null,
    via: via && (VIAS as readonly string[]).includes(via) ? (via as Via) : null,
    from: od && DATE.test(od) ? od : null,
    to: doParam && DATE.test(doParam) ? doParam : null,
  };
}

/** Stan filtrów → parametry adresu (puste pomijane; `wpis` zostaje, gdy podany). */
export function filtersToParams(f: AuditFilterState, entryId?: string | null): URLSearchParams {
  const p = new URLSearchParams();
  if (f.who) p.set('kto', f.who.replace(/^(user|type):/, ''));
  if (f.group) p.set('rodzaj', f.group);
  if (f.action) p.set('akcja', f.action);
  if (f.objectType) p.set('obiekt', f.objectType);
  if (f.clientId) p.set('klient', f.clientId);
  if (f.via) p.set('kanal', f.via);
  if (f.from) p.set('od', f.from);
  if (f.to) p.set('do', f.to);
  if (entryId) p.set('wpis', entryId);
  return p;
}

/** Stan filtrów → wejście `audit.list` / `audit.exportCsv` (zakres dat odwrócony — zamieniony miejscami). */
export function filtersToInput(f: AuditFilterState): Omit<ListAuditLogInput & object, 'limit' | 'cursor'> {
  const [from, to] = f.from && f.to && f.from > f.to ? [f.to, f.from] : [f.from, f.to];
  return {
    ...(f.who?.startsWith('user:') ? { actorUserId: f.who.slice(5) } : {}),
    ...(f.who?.startsWith('type:') ? { actorType: f.who.slice(5) as AuditActorType } : {}),
    ...(f.group ? { group: f.group } : {}),
    ...(f.action ? { action: f.action } : {}),
    ...(f.objectType ? { objectType: f.objectType } : {}),
    ...(f.clientId ? { clientId: f.clientId } : {}),
    ...(f.via ? { via: f.via } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };
}

export function activeFilterCount(f: AuditFilterState): number {
  return Object.values(f).filter((v) => v !== null).length;
}

/** Kto wykonał akcję: osoba albo rodzaj aktora („AI”, „Automat”, „System”). */
export function actorName(item: Pick<AuditLogItem, 'actor' | 'actorType'>): string {
  if (item.actor) return item.actor.name;
  return AUDIT_ACTOR_TYPE_LABELS[item.actorType as AuditActorType] ?? 'System';
}

/** Tekst wartości ukrytej („ukryte (sekret)”, „zmieniono (długi tekst)”). */
export function hiddenText(reason: keyof typeof AUDIT_HIDDEN_REASON_LABELS | undefined): string {
  return reason ? AUDIT_HIDDEN_REASON_LABELS[reason] : 'zmieniono';
}

/** Wpisy pogrupowane po dniu (Europe/Warsaw), w kolejności listy. */
export function groupByDay<T extends { at: Date | string }>(items: readonly T[]): { day: string; items: T[] }[] {
  const out: { day: string; items: T[] }[] = [];
  for (const item of items) {
    const day = toLocalDate(new Date(item.at));
    const last = out[out.length - 1];
    if (last && last.day === day) last.items.push(item);
    else out.push({ day, items: [item] });
  }
  return out;
}

/** Pobranie gotowego CSV (BOM już w treści) jako pliku. */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof URL.createObjectURL !== 'function') return;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}
